import { Link } from 'react-router-dom';
import { Icon } from '@/features/common/Icon';
import { NewsCardLink } from '@/features/publicsite/NewsCardLink';
import { publicNewsApi } from '@/features/publicsite/news/api/publicNewsApi';
import type { NewsItemsEnvelope } from '@/features/publicsite/types/NewsItemsEnvelope';
import { usePublicData } from '@/features/publicsite/usePublicList';
import { useLanguage } from '@/i18n/LanguageContext';
import { publicHome } from '@/i18n/dictionaries/publicHome';
import { useTranslation } from '@/i18n/useTranslation';

/** Module scope, so the fallback is one value rather than a new object on every render. */
const NO_NEWS: NewsItemsEnvelope = { items: [] };

/**
 * The three most recent stories.
 *
 * The only one of the four lists on this page that is cut server-side: asking the news endpoint
 * for a page of three saves sending the rest over the wire, where the other three endpoints answer
 * with everything and are sliced here.
 */
export function HomeNews() {
  const { t } = useTranslation(publicHome);
  const { language } = useLanguage();

  // The envelope as far as this strip is concerned (NewsItemsEnvelope): the home page asks for a
  // page of three and reads nothing but the items.
  const newsPage = usePublicData<NewsItemsEnvelope>(publicNewsApi.latestUrl, NO_NEWS);
  const news = newsPage?.items ?? null;

  return (
    <section className="section home-section--white">
      <div className="container">
        <div className="section-head">
          <div>
            <span className="eyebrow">{t('newsEyebrow')}</span>
            <h2>{t('newsHeading')}</h2>
          </div>
          <Link className="head-link" to="/stiri">
            {t('newsAll')} <Icon name="arrow-right" className="link-arrow" />
          </Link>
        </div>
        {news && news.length > 0 && (
          <div className="card-grid">
            {news.map((item) => (
              <NewsCardLink
                key={item.slug}
                item={item}
                defaultCategory={t('newsDefaultCategory')}
                language={language}
              />
            ))}
          </div>
        )}
        {news && news.length === 0 && <p className="empty-state">{t('emptyState')}</p>}
      </div>
    </section>
  );
}
